import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Card, ListGroup, Button, Form, Row, Col, Breadcrumb, Spinner, InputGroup, Alert } from 'react-bootstrap';
import { FaTrashAlt, FaEdit, FaExchangeAlt, FaSave, FaTimes, FaLightbulb, FaFileAlt, FaSyncAlt, FaCheck } from 'react-icons/fa';

const ModuleDetails = () => {
    const { id } = useParams();
    const [module, setModule] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [newFiles, setNewFiles] = useState({ type_document: 'Cours', document_title: '', files: [] });
    const [summarizing, setSummarizing] = useState(null);
    const [generatingOpinion, setGeneratingOpinion] = useState(null);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [replacingDocId, setReplacingDocId] = useState(null);
    const [selectedReplaceFile, setSelectedReplaceFile] = useState(null);
    const [replacingStatus, setReplacingStatus] = useState('idle'); // idle, uploading, success, error
    const replaceFileRef = useRef(null);

    const fetchDocuments = useCallback(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/documents/${id}`)
            .then(res => setDocuments(res.data))
            .catch(err => console.log(err));
    }, [id]);

    useEffect(() => {
        // Fetch module details
        axios.get(`${process.env.REACT_APP_API_URL}/api/modules/${id}`)
            .then(res => setModule(res.data))
            .catch(err => console.log(err));

        fetchDocuments();
    }, [id, fetchDocuments]);

    const handleInputChange = (e) => {
        setNewFiles({ ...newFiles, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        setNewFiles({ ...newFiles, files: e.target.files });
    };

    const handleAddDocuments = () => {
        const formData = new FormData();
        formData.append('type_document', newFiles.type_document);
        if (newFiles.document_title) {
            formData.append('document_title', newFiles.document_title);
        }
        for (let i = 0; i < newFiles.files.length; i++) {
            formData.append('files', newFiles.files[i]);
        }

        axios.post(`${process.env.REACT_APP_API_URL}/api/documents/${id}`, formData)
            .then(res => {
                setDocuments([...documents, ...res.data]);
                // Reset the form after successful upload
                setNewFiles({ type_document: 'Cours', document_title: '', files: [] });
            })
            .catch(err => console.log(err));
    };

    const handleDeleteDocument = (docId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.')) {
            axios.delete(`${process.env.REACT_APP_API_URL}/api/documents/${docId}`)
                .then(() => setDocuments(documents.filter(doc => doc.id !== docId)))
                .catch(err => console.log(err));
        }
    };

    const handleSummarize = (docId) => {
        setSummarizing(docId);
        axios.post(`${process.env.REACT_APP_API_URL}/api/documents/${docId}/summarize`)
            .then(res => {
                // Start polling for summary status
                pollSummaryStatus(docId);
            })
            .catch(err => {
                console.log(err);
                setSummarizing(null); // Clear summarizing state on error
            });
    };

    const pollSummaryStatus = (docId) => {
        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/documents/single/${docId}`);
                const updatedDoc = res.data;

                if (updatedDoc.summarization_status === 'completed' || updatedDoc.summarization_status === 'failed') {
                    clearInterval(interval); // Stop polling
                    setSummarizing(null); // Clear summarizing state
                    fetchDocuments(); // Re-fetch all documents to update the list
                }
            } catch (err) {
                console.error('Error polling summary status:', err);
                clearInterval(interval); // Stop polling on error
                setSummarizing(null); // Clear summarizing state on error
            }
        }, 5000); // Poll every 5 seconds
    };

    const pollOpinionStatus = (docId) => {
        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/documents/single/${docId}`);
                const updatedDoc = res.data;

                if (updatedDoc.opinion_status === 'completed' || updatedDoc.opinion_status === 'failed') {
                    clearInterval(interval); // Stop polling
                    setGeneratingOpinion(null); // Clear generatingOpinion state
                    fetchDocuments(); // Re-fetch all documents to update the list
                }
            } catch (err) {
                console.error('Error polling opinion status:', err);
                clearInterval(interval); // Stop polling on error
                setGeneratingOpinion(null); // Clear generatingOpinion state on error
            }
        }, 5000); // Poll every 5 seconds
    };

    const handleGenerateOpinion = (docId) => {
        setGeneratingOpinion(docId);
        axios.post(`${process.env.REACT_APP_API_URL}/api/documents/${docId}/opinion`)
            .then(res => {
                // Start polling for opinion status
                pollOpinionStatus(docId);
            })
            .catch(err => {
                console.error('Error generating opinion:', err);
                alert('Erreur lors de la génération de l\'avis.');
                setGeneratingOpinion(null);
            });
    };

    const handleViewOpinion = (opinionText) => {
        alert(opinionText); // Simple alert for now
    };

    const handleEditClick = (doc) => {
        setEditingDocId(doc.id);
        setEditingTitle(doc.titre_affiche || doc.nom);
    };


    const handleTitleChange = (e) => {
        setEditingTitle(e.target.value);
    };

    const handleTitleUpdate = (docId) => {
        axios.put(`${process.env.REACT_APP_API_URL}/api/documents/${docId}/title`, { title: editingTitle })
            .then(res => {
                setEditingDocId(null);
                setEditingTitle('');
                fetchDocuments();
            })
            .catch(err => console.log(err));
    };

    const handleReplaceClick = (docId) => {
        if (window.confirm('Êtes-vous sûr de vouloir remplacer ce document ? L\'ancien fichier sera supprimé si non utilisé ailleurs et le nouveau sera téléchargé.')) {
            setReplacingDocId(docId);
            setSelectedReplaceFile(null); // Clear previously selected file
            setReplacingStatus('idle'); // Reset status when starting a new replacement
        }
    };

    const handleReplaceFileChange = () => {
        if (replaceFileRef.current && replaceFileRef.current.files.length > 0) {
            setSelectedReplaceFile(replaceFileRef.current.files[0]);
        } else {
            setSelectedReplaceFile(null);
        }
    };

    const confirmReplaceDocument = (docId) => {
        console.log('confirmReplaceDocument called with docId:', docId);
        if (!selectedReplaceFile) {
            alert('Veuillez sélectionner un fichier à télécharger.');
            console.log('No file selected');
            return;
        }

        console.log('Selected file:', selectedReplaceFile);
        setReplacingStatus('uploading');
        const formData = new FormData();
        formData.append('file', selectedReplaceFile);
        // Preserve document type. Find the doc being replaced to get its type.
        const docToReplace = documents.find(doc => doc.id === docId);
        if(docToReplace) {
            formData.append('type_document', docToReplace.type_document);
        }

        console.log('Sending PUT request to /api/documents/:id/replace');
        axios.put(`${process.env.REACT_APP_API_URL}/api/documents/${docId}/replace`, formData)
            .then(res => {
                console.log('Replacement successful:', res.data);
                setReplacingStatus('success');
                setTimeout(() => {
                    setReplacingDocId(null);
                    setSelectedReplaceFile(null);
                    fetchDocuments();
                    setReplacingStatus('idle');
                }, 2000); // Show success message for 2 seconds
            })
            .catch(err => {
                console.error('Erreur lors du remplacement du document:', err);
                setReplacingStatus('error');
            });
    };


    const renderDocumentsByType = (type) => {
        const filteredDocs = documents.filter(doc => doc.type_document === type);
        return (
            <ListGroup variant="flush">
                {filteredDocs.map(doc => (
                    <ListGroup.Item key={doc.id} className="d-flex justify-content-between align-items-center">
                        <div>
                            {editingDocId === doc.id ? (
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        value={editingTitle}
                                        onChange={handleTitleChange}
                                    />
                                    <Button variant="success" onClick={() => handleTitleUpdate(doc.id)}><FaSave /></Button>
                                    <Button variant="secondary" onClick={() => setEditingDocId(null)}><FaTimes /></Button>
                                </InputGroup>
                            ) : replacingDocId === doc.id ? (
                                <div>
                                    <p className="mb-2">Sélectionnez un nouveau fichier et cliquez sur 'Confirmer'.</p>
                                    <InputGroup>
                                        <Form.Control type="file" ref={replaceFileRef} onChange={handleReplaceFileChange} />
                                        <Button 
                                            variant="success" 
                                            onClick={() => confirmReplaceDocument(doc.id)} 
                                            disabled={!selectedReplaceFile || replacingStatus === 'uploading'}
                                        >
                                            {replacingStatus === 'uploading' ? <Spinner as="span" animation="border" size="sm" /> : <FaCheck />}
                                        </Button>
                                        <Button variant="secondary" onClick={() => setReplacingDocId(null)}><FaTimes /></Button>
                                    </InputGroup>
                                    {replacingStatus === 'success' && <Alert variant="success" className="mt-2">Document remplacé avec succès!</Alert>}
                                    {replacingStatus === 'error' && <Alert variant="danger" className="mt-2">Échec du remplacement du document.</Alert>}
                                </div>
                            ) : (
                                <>
                                    <a href={`${process.env.REACT_APP_API_URL}/${doc.chemin_fichier}`} download>{doc.titre_affiche || doc.nom}</a>
                                    <div className="text-muted small">Original: {doc.nom} | Ajouté le: {new Date(doc.date_ajout).toLocaleDateString()}</div>
                                </>
                            )}
                        </div>
                        <div className="d-flex align-items-center">
                            <FaTrashAlt color="red" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Supprimer" onClick={() => handleDeleteDocument(doc.id)} />
                            <FaEdit color="blue" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Modifier" onClick={() => handleEditClick(doc)} />
                            <FaExchangeAlt color="orange" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Remplacer" onClick={() => handleReplaceClick(doc.id)} />

                            {['.pdf', '.pptx', '.ppt', '.docx', '.doc', '.txt'].includes(doc.extension) && (
                                doc.opinion && doc.opinion_status === 'completed' ? (
                                    <FaLightbulb color="yellow" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Voir Opinion" onClick={() => handleViewOpinion(doc.opinion)} />
                                ) : (
                                    <FaLightbulb color="grey" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Avis" onClick={() => handleGenerateOpinion(doc.id)} disabled={generatingOpinion === doc.id} />
                                )
                            )}

                            {['.pdf', '.pptx', '.ppt', '.docx', '.doc', '.txt'].includes(doc.extension) && (
                                (() => {
                                    switch (doc.summarization_status) {
                                        case 'pending':
                                            return (
                                                <FaSyncAlt color="blue" size={20} className="ms-2" spin title="Résumé en cours..." />
                                            );
                                        case 'completed':
                                            return (
                                                <Link to={`/summary/${doc.id}`} title="Voir Résumé">
                                                    <FaFileAlt color="green" size={20} className="ms-2" style={{cursor: 'pointer'}} />
                                                </Link>
                                            );
                                        case 'failed':
                                            return (
                                                <FaSyncAlt color="red" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Échec du résumé (Réessayer)" onClick={() => handleSummarize(doc.id)} />
                                            );
                                        default: // 'none' or any other status
                                            return (
                                                <FaSyncAlt color="blue" size={20} className="ms-2" style={{cursor: 'pointer'}} title="Résumer" onClick={() => handleSummarize(doc.id)} disabled={summarizing === doc.id} />
                                            );
                                    }
                                })()
                            )}
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        );
    };

    if (!module) return <div>Loading...</div>;

    return (
        <div>
            <Breadcrumb className="mt-4">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>Modules</Breadcrumb.Item>
                <Breadcrumb.Item active>{module.nom}</Breadcrumb.Item>
            </Breadcrumb>

            <Card className="mt-2">
                <Card.Header><h2>{module.nom}</h2></Card.Header>
                <Card.Body>
                    <Card.Text><strong>Type:</strong> {module.type}</Card.Text>
                    <Card.Text><strong>Volume horaire:</strong> {module.volume_horaire}h</Card.Text>
                </Card.Body>
            </Card>

            <Card className="mt-4">
                <Card.Header><h4>Ajouter un document</h4></Card.Header>
                <Card.Body>
                    <Form as={Row}>
                        <Col md={3}>
                            <Form.Control as="select" name="type_document" onChange={handleInputChange} value={newFiles.type_document}>
                                <option value="Cours">Cours</option>
                                <option value="TD">TD</option>
                                <option value="TP et corrections">TP et corrections</option>
                            </Form.Control>
                        </Col>
                        <Col md={4}>
                            <Form.Control
                                type="text"
                                name="document_title"
                                placeholder="Titre du document"
                                onChange={handleInputChange}
                                value={newFiles.document_title}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Control type="file" multiple onChange={handleFileChange} />
                        </Col>
                        <Col md={2}>
                            <Button onClick={handleAddDocuments} className="w-100">Ajouter</Button>
                        </Col>
                    </Form>
                </Card.Body>
            </Card>

            <Row className="mt-4">
                <Col md={4}>
                    <Card>
                        <Card.Header><h5>Cours</h5></Card.Header>
                        {renderDocumentsByType('Cours')}
                    </Card>
                </Col>
                <Col md={4}>
                    <Card>
                        <Card.Header><h5>TD</h5></Card.Header>
                        {renderDocumentsByType('TD')}
                    </Card>
                </Col>
                <Col md={4}>
                    <Card>
                        <Card.Header><h5>TP et corrections</h5></Card.Header>
                        {renderDocumentsByType('TP et corrections')}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ModuleDetails;
